import esriRequest from '@arcgis/core/request.js';
import Geometry from '@arcgis/core/geometry/Geometry.js';
import Extent from '@arcgis/core/geometry/Extent.js';
import Point from '@arcgis/core/geometry/Point.js';
import Polygon from '@arcgis/core/geometry/Polygon.js';
import Polyline from '@arcgis/core/geometry/Polyline.js';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine.js';
import SpatialReference from '@arcgis/core/geometry/SpatialReference.js';
import type FeatureLayer from '@arcgis/core/layers/FeatureLayer.js';

import { findLayer } from '@/map/layers/registry';
import { getOrCreateFeatureLayer } from '@/map/layers/factory';
import { getMapView } from '@/map/mapStore';
import { getSelectionGeometry, useSelectionStore } from '@/state/selectionStore';
import { resolveFieldName } from '@/agent/tools/shared/layers';
import { getHandle } from './handles';
import type { Source } from './source';

const GEOCODE_URL =
  'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates';

const POINT_EXTENT_METERS = 100;

export type GeocodeCandidate = {
  address: string;
  score: number;
  geometry: Point;
  extent?: Extent;
};

export type ResolvedFeature = {
  layer_id: string;
  objectid: number;
  attributes: Record<string, unknown>;
  geometry: Geometry;
};

export type ResolvedFeatureSet = {
  layer_id: string;
  where: string;
  count: number;
  objectids: number[];
  geometries: Geometry[];
};

export type ResolvedRef = {
  source_kind: Source['kind'];
  geometry?: Geometry;
  extent?: Extent;
  feature?: ResolvedFeature;
  feature_set?: ResolvedFeatureSet;
  candidates?: GeocodeCandidate[];
};

export type ResolveOptions = {
  needsGeometry?: boolean;
  outFields?: string[];
};

export async function resolve(
  sourceInput: Source | string,
  opts: ResolveOptions = {},
): Promise<ResolvedRef> {
  const source = normalizeSource(sourceInput);

  switch (source.kind) {
    case 'place':
      return resolvePlace(source);
    case 'feature':
      return resolveFeature(source);
    case 'feature_set':
      return resolveFeatureSet(source, opts);
    case 'geometry':
      return resolveGeometry(source);
    case 'extent':
      return resolveExtent(source);
    case 'selection':
      return resolveSelection();
    case 'view':
      return resolveView();
    case 'handle': {
      const ref = getHandle(source.id);
      if (!ref) throw new Error(`Handle "${source.id}" expired or unknown`);
      return ref;
    }
    default:
      throw new Error(`Invalid source.kind: "${String((source as { kind?: unknown }).kind)}"`);
  }
}

function normalizeSource(sourceInput: Source | string): Source {
  let candidate: unknown = sourceInput;

  if (typeof candidate === 'string') {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      throw new Error('Invalid source: expected object or JSON stringified object');
    }
  }

  if (!isRecord(candidate)) {
    throw new Error('Invalid source: expected object');
  }

  const kind = candidate.kind;
  if (typeof kind !== 'string') {
    throw new Error('Invalid source: missing "kind"');
  }

  const allowedKinds: Source['kind'][] = [
    'place',
    'feature',
    'feature_set',
    'geometry',
    'extent',
    'selection',
    'view',
    'handle',
  ];
  if (!allowedKinds.includes(kind as Source['kind'])) {
    throw new Error(`Invalid source.kind: "${kind}"`);
  }

  return candidate as Source;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

async function resolvePlace(
  source: Extract<Source, { kind: 'place' }>,
): Promise<ResolvedRef> {
  const max = Math.max(1, Math.min(10, source.max_results ?? 5));
  const res = await esriRequest(GEOCODE_URL, {
    query: { f: 'json', SingleLine: source.text, maxLocations: String(max), outSR: 4326 },
    responseType: 'json',
  });
  const data = res.data as {
    candidates?: Array<{
      address: string;
      score: number;
      location: { x: number; y: number };
      extent?: { xmin: number; ymin: number; xmax: number; ymax: number };
    }>;
  };
  const list = data.candidates ?? [];
  if (list.length === 0) {
    throw new Error(`No geocode results for "${source.text}"`);
  }
  const sr = new SpatialReference({ wkid: 4326 });
  const candidates: GeocodeCandidate[] = list.map((c) => ({
    address: c.address,
    score: c.score,
    geometry: new Point({ x: c.location.x, y: c.location.y, spatialReference: sr }),
    extent: c.extent
      ? new Extent({ ...c.extent, spatialReference: sr })
      : undefined,
  }));
  const top = candidates[0];
  return {
    source_kind: 'place',
    geometry: top.geometry,
    extent: top.extent ?? extentForPoint(top.geometry, POINT_EXTENT_METERS),
    candidates,
  };
}

async function resolveFeature(
  source: Extract<Source, { kind: 'feature' }>,
): Promise<ResolvedRef> {
  const layer = await ensureFeatureLayer(source.layer_id);
  const oidField = layer.objectIdField || 'objectid';
  const rs = await layer.queryFeatures({
    where: `${oidField} = ${source.objectid}`,
    outFields: ['*'],
    returnGeometry: true,
    num: 1,
  });
  const feat = rs.features[0];
  if (!feat || !feat.geometry) {
    throw new Error(`Feature ${source.objectid} not found in layer "${source.layer_id}"`);
  }
  return {
    source_kind: 'feature',
    geometry: feat.geometry,
    extent: extentOf(feat.geometry),
    feature: {
      layer_id: source.layer_id,
      objectid: source.objectid,
      attributes: feat.attributes ?? {},
      geometry: feat.geometry,
    },
  };
}

async function resolveFeatureSet(
  source: Extract<Source, { kind: 'feature_set' }>,
  opts: ResolveOptions,
): Promise<ResolvedRef> {
  const layer = await ensureFeatureLayer(source.layer_id);
  const record = findLayer(source.layer_id);
  const orderBy = resolveFieldName(source.order_by, record?.probe, layer);
  const oidField = layer.objectIdField || 'objectid';
  // When a spatial_filter is set the user has already constrained the result; raise the
  // default so a typical "features inside a buffer" query is not silently truncated at 500.
  const limitDefault = source.spatial_filter ? 5000 : 500;
  const limit = source.limit && source.limit > 0 ? source.limit : limitDefault;
  const orderByFields = orderBy
    ? [`${orderBy} ${source.order_dir ?? 'ASC'}`]
    : undefined;
  const view = source.in_current_extent ? getMapView() : null;
  const explicitSpatial = source.spatial_filter
    ? await resolveSpatialFilterGeometry(source.spatial_filter)
    : undefined;
  const spatialGeom = explicitSpatial ?? view?.extent ?? undefined;

  const rs = await layer.queryFeatures({
    where: source.where || '1=1',
    outFields: opts.outFields ?? ['*'],
    returnGeometry: opts.needsGeometry !== false,
    orderByFields,
    num: limit,
    geometry: spatialGeom,
    spatialRelationship: spatialGeom ? 'intersects' : undefined,
  });

  const objectids: number[] = [];
  const geometries: Geometry[] = [];
  for (const f of rs.features) {
    const oid = f.attributes?.[oidField];
    if (typeof oid === 'number') objectids.push(oid);
    if (f.geometry) geometries.push(f.geometry);
  }

  const unioned = unionGeometries(geometries);
  const ext = unioned ? extentOf(unioned) : undefined;

  return {
    source_kind: 'feature_set',
    geometry: unioned ?? undefined,
    extent: ext,
    feature_set: {
      layer_id: source.layer_id,
      where: source.where || '1=1',
      count: rs.features.length,
      objectids,
      geometries,
    },
  };
}

async function resolveSpatialFilterGeometry(source: Source): Promise<Geometry | undefined> {
  const ref = await resolve(source);
  return ref.geometry ?? ref.extent ?? undefined;
}

function resolveGeometry(source: Extract<Source, { kind: 'geometry' }>): ResolvedRef {
  const geom = geometryFromJson(source.geometry);
  return { source_kind: 'geometry', geometry: geom, extent: extentOf(geom) };
}

function resolveExtent(source: Extract<Source, { kind: 'extent' }>): ResolvedRef {
  const ext = new Extent({
    xmin: source.xmin,
    ymin: source.ymin,
    xmax: source.xmax,
    ymax: source.ymax,
    spatialReference: new SpatialReference({ wkid: source.wkid ?? 4326 }),
  });
  return { source_kind: 'extent', extent: ext, geometry: ext };
}

function resolveView(): ResolvedRef {
  const view = getMapView();
  const ext = view?.extent;
  if (!ext) {
    throw new Error('view: map is not initialised or has no extent yet');
  }
  return { source_kind: 'view', extent: ext, geometry: ext };
}

function resolveSelection(): ResolvedRef {
  const sel = useSelectionStore.getState().current;
  const geom = getSelectionGeometry();
  if (!sel || !geom) {
    throw new Error('No feature is currently selected — ask the user to click one first.');
  }
  return {
    source_kind: 'selection',
    geometry: geom,
    extent: extentOf(geom),
    feature: {
      layer_id: sel.layer_id,
      objectid: typeof sel.objectid === 'number' ? sel.objectid : Number(sel.objectid),
      attributes: sel.attributes,
      geometry: geom,
    },
  };
}

async function ensureFeatureLayer(layerId: string): Promise<FeatureLayer> {
  const record = findLayer(layerId);
  if (!record) throw new Error(`Unknown layer id: "${layerId}"`);
  const layer = getOrCreateFeatureLayer(record);
  await layer.load().catch(() => undefined);
  return layer;
}

function extentOf(geom: Geometry): Extent {
  if (geom.type === 'extent') return geom as Extent;
  const ext = (geom as { extent?: Extent }).extent;
  if (ext) return ext;
  if (geom.type === 'point') {
    return extentForPoint(geom as Point, POINT_EXTENT_METERS);
  }
  throw new Error(`Cannot derive extent for geometry of type "${geom.type}"`);
}

function extentForPoint(p: Point, halfMeters: number): Extent {
  const sr = p.spatialReference ?? new SpatialReference({ wkid: 4326 });
  const isGeographic = sr.wkid === 4326 || sr.wkid === 4269;
  const dx = isGeographic ? halfMeters / 111_000 : halfMeters;
  return new Extent({
    xmin: p.x - dx,
    ymin: p.y - dx,
    xmax: p.x + dx,
    ymax: p.y + dx,
    spatialReference: sr,
  });
}

type GeometryUnionInput = Parameters<typeof geometryEngine.union>[0];

function unionGeometries(list: Geometry[]): Geometry | null {
  if (list.length === 0) return null;
  if (list.length === 1) return list[0];
  try {
    const result = geometryEngine.union(list as unknown as GeometryUnionInput);
    return (Array.isArray(result) ? result[0] : result) as Geometry;
  } catch {
    return list[0];
  }
}

function geometryFromJson(json: Record<string, unknown>): Geometry {
  if ('rings' in json) return Polygon.fromJSON(json);
  if ('paths' in json) return Polyline.fromJSON(json);
  if ('xmin' in json && 'xmax' in json) return Extent.fromJSON(json);
  if ('x' in json && 'y' in json) return Point.fromJSON(json);
  throw new Error('Unrecognized geometry JSON shape');
}

export type HandleSummary = {
  source_kind: Source['kind'];
  geometry_type?: Geometry['type'];
  centroid?: { x: number; y: number; wkid?: number };
  extent?: { xmin: number; ymin: number; xmax: number; ymax: number; wkid?: number };
  feature_count?: number;
};

export function summarizeRef(ref: ResolvedRef): HandleSummary {
  const summary: HandleSummary = { source_kind: ref.source_kind };
  if (ref.geometry) summary.geometry_type = ref.geometry.type;
  if (ref.extent) {
    summary.extent = {
      xmin: ref.extent.xmin,
      ymin: ref.extent.ymin,
      xmax: ref.extent.xmax,
      ymax: ref.extent.ymax,
      wkid: ref.extent.spatialReference?.wkid ?? undefined,
    };
    const center = ref.extent.center;
    if (center) {
      summary.centroid = {
        x: center.x,
        y: center.y,
        wkid: center.spatialReference?.wkid ?? undefined,
      };
    }
  } else if (ref.geometry?.type === 'point') {
    const p = ref.geometry as Point;
    summary.centroid = {
      x: p.x,
      y: p.y,
      wkid: p.spatialReference?.wkid ?? undefined,
    };
  }
  if (ref.feature_set) summary.feature_count = ref.feature_set.count;
  return summary;
}
